import { Head, Link, router, usePage } from "@inertiajs/react";
import { Box, Button, Callout, Flex, Heading, Select, Separator, Table, Text, TextField } from "@radix-ui/themes";
import type { CSSProperties } from "react";
import { useEffect, useMemo, useState } from "react";

import { AppLayout } from "@/components/app-layout";
import type { AuthUser, SharedProps } from "@/lib/types";

import {
  bambooEmployeeDisplayLabel,
  buildWeekDates,
  emptyTimeEntryMatrixRow,
  emailsMatch,
  ensureScenarioStableIds,
  harvestProjectDisplayLabel,
  type HarvestTimeEntryRow,
  harvestUserDisplayLabel,
  isPickableBambooEmployee,
  isPickableHarvestProject,
  isPickableHarvestUser,
  matrixRowsToTimeEntries,
  nz,
  randomWeekHoursUnderCap,
  recalculateBudgetSpentFromTimeEntries,
  sortTimeEntryMatrixRows,
  syncEmployeesFromHarvestUsers,
  timeEntriesToMatrixRows,
  updateMatrixCell,
} from "./helpers";
import type { HarvestProjectRow, HarvestUserRow } from "./helpers";

type HarvestUser = HarvestUserRow & { baseline_weekly_hours: string };
type HarvestProject = HarvestProjectRow;

type TimeEntry = HarvestTimeEntryRow;

type BambooEmployee = {
  id: string;
  workEmail: string;
  displayName: string;
  firstName: string;
  lastName: string;
};

type Scenario = {
  schema_version: number;
  harvest: {
    users: HarvestUser[];
    projects: HarvestProject[];
    time_entries: TimeEntry[];
    budget_rows: Record<string, unknown>[];
    healthcheck_user_id: string;
  };
  bamboo: {
    subdomain: string;
    employees: BambooEmployee[];
    time_off_by_employee: Record<string, unknown[]>;
    sync_week_starts: string[];
  };
};

type MockIntegrationShowProps = {
  auth: { user: AuthUser | null };
  flash?: { notice?: string; alert?: string };
  scenario: Scenario;
  scenarioPersistedInRedis: boolean;
  mockserverDashboardUrl: string;
};

/** Radix Select placeholder — must not collide with real Harvest ids / emails */
const SELECT_NONE = "__mock_integration_none__";
const PROJECT_FILTER_ALL = "__mock_integration_all_projects__";

function dateInputStyle(invalid: boolean): CSSProperties {
  return {
    width: "100%",
    minHeight: 32,
    borderRadius: "var(--radius-2)",
    border: invalid ? "1px solid var(--red-9)" : "1px solid var(--gray-a7)",
    padding: "0 8px",
    fontSize: 14,
    background: "var(--gray-2)",
  };
}

function emptyUser(): HarvestUser {
  return { id: "", email: "", first_name: "", last_name: "", baseline_weekly_hours: "40" };
}

function emptyProject(): HarvestProject {
  return { id: "", name: "", budget: "", budget_spent: "", budget_spent_anchor: "", starts_on: "" };
}

function emptyTimeEntry(): TimeEntry {
  return { user_harvest_id: "", project_harvest_id: "", spent_date: "", hours: "0" };
}

function isoWeekStartForDate(isoDate: string): string | null {
  const s = nz(isoDate).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7));
  const d = Number(s.slice(8, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  const day = dt.getUTCDay(); // 0 Sun .. 6 Sat
  const delta = day === 0 ? -6 : 1 - day;
  dt.setUTCDate(dt.getUTCDate() + delta);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function defaultWeekStartFromScenario(s: Scenario): string {
  const firstDate = s.harvest.time_entries.map((t) => nz(t.spent_date).trim()).find(Boolean);
  const fromEntry = firstDate ? isoWeekStartForDate(firstDate) : null;
  if (fromEntry) return fromEntry;
  const now = new Date();
  const day = now.getDay();
  const delta = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + delta);
  const yy = monday.getFullYear();
  const mm = String(monday.getMonth() + 1).padStart(2, "0");
  const dd = String(monday.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function emptyEmployee(): BambooEmployee {
  return { id: "", workEmail: "", displayName: "", firstName: "", lastName: "" };
}

/** YYYY-MM-DD only; rejects 2026-02-31 etc. Empty string is valid. */
function isoCalendarDateError(raw: string | null | undefined): string | null {
  const s = nz(raw).trim();
  if (!s) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "Use YYYY-MM-DD";
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7));
  const d = Number(s.slice(8, 10));
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
    return "Invalid calendar date";
  }
  return null;
}

const PTO_STATUS_PRESETS = [
  { value: "approved", id: "approved", name: "Approved" },
  { value: "pending", id: "pending", name: "Pending" },
  { value: "denied", id: "denied", name: "Denied" },
] as const;

type BambooPtoRow = {
  rowKey: string;
  employeeId: string;
  start: string;
  end: string;
  statusPreset: string;
  customStatusId: string;
  customStatusName: string;
};

function newPtoRowKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `pto-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function emptyPtoRow(): BambooPtoRow {
  return {
    rowKey: newPtoRowKey(),
    employeeId: "",
    start: "",
    end: "",
    statusPreset: "approved",
    customStatusId: "",
    customStatusName: "",
  };
}

function statusForPayload(row: BambooPtoRow): { id: string; name: string } {
  if (row.statusPreset === "custom") {
    return {
      id: nz(row.customStatusId).trim() || "approved",
      name: nz(row.customStatusName).trim() || "Approved",
    };
  }
  const preset = PTO_STATUS_PRESETS.find((p) => p.value === row.statusPreset);
  return { id: preset?.id ?? "approved", name: preset?.name ?? "Approved" };
}

function timeOffHashToRows(hash: Record<string, unknown>): BambooPtoRow[] {
  const rows: BambooPtoRow[] = [];
  for (const [empId, arr] of Object.entries(hash)) {
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      if (!item || typeof item !== "object") continue;
      const o = item as Record<string, unknown>;
      const st = o.status && typeof o.status === "object" ? (o.status as Record<string, unknown>) : {};
      const id = String(st.id ?? "approved");
      const name = String(st.name ?? "Approved");
      const match = PTO_STATUS_PRESETS.find((p) => p.id === id && p.name === name);
      rows.push({
        rowKey: newPtoRowKey(),
        employeeId: empId,
        start: String(o.start ?? "").trim(),
        end: String(o.end ?? "").trim(),
        statusPreset: match ? match.value : "custom",
        customStatusId: match ? "" : id,
        customStatusName: match ? "" : name,
      });
    }
  }
  return rows;
}

function timeOffRowsToHash(rows: BambooPtoRow[]): Record<string, unknown[]> {
  const out: Record<string, unknown[]> = {};
  for (const row of rows) {
    const emp = nz(row.employeeId).trim();
    const start = nz(row.start).trim();
    const end = nz(row.end).trim();
    if (!emp || !start || !end) continue;
    if (!out[emp]) out[emp] = [];
    const st = statusForPayload(row);
    out[emp].push({
      start,
      end,
      status: { id: st.id, name: st.name },
    });
  }
  return out;
}

function validateTimeOffRows(rows: BambooPtoRow[]): string | null {
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const touched = nz(r.employeeId).trim() || nz(r.start).trim() || nz(r.end).trim();
    if (!touched) continue;
    if (!nz(r.employeeId).trim()) return `PTO row ${i + 1}: choose an employee`;
    if (!nz(r.start).trim() || !nz(r.end).trim()) return `PTO row ${i + 1}: start and end dates are required`;
    const es = isoCalendarDateError(r.start);
    if (es) return `PTO row ${i + 1} start: ${es}`;
    const ee = isoCalendarDateError(r.end);
    if (ee) return `PTO row ${i + 1} end: ${ee}`;
    if (r.statusPreset === "custom") {
      if (!nz(r.customStatusId).trim()) return `PTO row ${i + 1}: custom status id is required`;
      if (!nz(r.customStatusName).trim()) return `PTO row ${i + 1}: custom status name is required`;
    }
  }
  return null;
}

function normalizeScenario(raw: Scenario): Scenario {
  return {
    schema_version: raw.schema_version ?? 1,
    harvest: {
      users: (raw.harvest?.users ?? []).map((u) => {
        const m = { ...emptyUser(), ...(u as HarvestUser) };
        return {
          id: nz(m.id),
          email: nz(m.email),
          first_name: nz(m.first_name),
          last_name: nz(m.last_name),
          baseline_weekly_hours: nz(m.baseline_weekly_hours) || "40",
        };
      }),
      projects: (raw.harvest?.projects ?? []).map((p) => {
        const m = { ...emptyProject(), ...(p as HarvestProject) };
        return {
          id: nz(m.id),
          name: nz(m.name),
          budget: nz(m.budget),
          budget_spent: nz(m.budget_spent),
          budget_spent_anchor: nz((m as HarvestProject).budget_spent_anchor),
          starts_on: nz(m.starts_on),
        };
      }),
      time_entries: (raw.harvest?.time_entries ?? []).map((t) => {
        const m = { ...emptyTimeEntry(), ...(t as TimeEntry) };
        return {
          user_harvest_id: nz(m.user_harvest_id),
          project_harvest_id: nz(m.project_harvest_id),
          spent_date: nz(m.spent_date),
          hours: nz(m.hours) || "0",
        };
      }),
      budget_rows: raw.harvest?.budget_rows ?? [],
      healthcheck_user_id: nz(raw.harvest?.healthcheck_user_id),
    },
    bamboo: {
      subdomain: nz(raw.bamboo?.subdomain) || "acme",
      employees: (raw.bamboo?.employees ?? []).map((e) => {
        const m = { ...emptyEmployee(), ...(e as BambooEmployee) };
        return {
          id: nz(m.id),
          workEmail: nz(m.workEmail),
          displayName: nz(m.displayName),
          firstName: nz(m.firstName),
          lastName: nz(m.lastName),
        };
      }),
      time_off_by_employee: raw.bamboo?.time_off_by_employee ?? {},
      sync_week_starts: (raw.bamboo?.sync_week_starts ?? []).map((w) => nz(w)),
    },
  };
}

function scenarioToPayload(s: Scenario) {
  return {
    schema_version: 1,
    harvest: {
      users: s.harvest.users
        .filter((u) => nz(u.id).trim())
        .map((u) => ({
          id: u.id,
          email: u.email,
          first_name: u.first_name,
          last_name: u.last_name,
          baseline_weekly_hours: Number.parseFloat(u.baseline_weekly_hours) || 40,
        })),
      projects: s.harvest.projects
        .filter((p) => nz(p.id).trim())
        .map((p) => ({
          id: p.id,
          name: p.name,
          budget: p.budget === "" ? null : Number.parseFloat(p.budget),
          budget_spent: p.budget_spent === "" ? null : Number.parseFloat(p.budget_spent),
          starts_on: nz(p.starts_on).trim() || null,
        })),
      time_entries: s.harvest.time_entries
        .filter((t) => nz(t.user_harvest_id).trim() && nz(t.project_harvest_id).trim() && nz(t.spent_date).trim())
        .map((t) => ({
          user_harvest_id: t.user_harvest_id,
          project_harvest_id: t.project_harvest_id,
          spent_date: t.spent_date,
          hours: Number.parseFloat(t.hours) || 0,
        })),
      budget_rows: s.harvest.budget_rows,
      healthcheck_user_id: null,
    },
    bamboo: {
      subdomain: nz(s.bamboo.subdomain).trim() || "acme",
      employees: s.bamboo.employees
        .filter((e) => nz(e.id).trim())
        .map((e) => ({
          id: e.id,
          workEmail: e.workEmail,
          displayName: e.displayName,
          firstName: e.firstName,
          lastName: e.lastName,
        })),
      time_off_by_employee: s.bamboo.time_off_by_employee,
      sync_week_starts: s.bamboo.sync_week_starts,
    },
  };
}

/** Stable JSON for dirty-check vs server baseline (same shape as save payload, including PTO rows). */
function comparableScenarioPayload(s: Scenario, ptoRows: BambooPtoRow[]): string {
  const timeOff = timeOffRowsToHash(ptoRows);
  return JSON.stringify(
    scenarioToPayload({
      ...s,
      bamboo: {
        ...s.bamboo,
        time_off_by_employee: timeOff,
        sync_week_starts: s.bamboo.sync_week_starts,
      },
    }),
  );
}

export default function MockIntegrationShow({
  auth,
  scenario: initialScenario,
  scenarioPersistedInRedis,
  mockserverDashboardUrl,
  flash,
}: MockIntegrationShowProps) {
  const page = usePage<MockIntegrationShowProps & SharedProps>();
  const appClockTodayIso = page.props.app_clock?.today_iso8601;

  const serverScenarioKey = useMemo(() => JSON.stringify(initialScenario), [ initialScenario ]);

  const [scenario, setScenario] = useState(() => ensureScenarioStableIds(normalizeScenario(initialScenario)));
  const [timeOffRows, setTimeOffRows] = useState<BambooPtoRow[]>(() => {
    const fromHash = timeOffHashToRows(initialScenario.bamboo?.time_off_by_employee ?? {});
    return fromHash.length > 0 ? fromHash : [ emptyPtoRow() ];
  });
  const [timeEntryWeekStart, setTimeEntryWeekStart] = useState<string>(() =>
    defaultWeekStartFromScenario(normalizeScenario(initialScenario)),
  );
  const [timeEntryProjectFilter, setTimeEntryProjectFilter] = useState<string>(PROJECT_FILTER_ALL);
  const [timeEntryMatrixRows, setTimeEntryMatrixRows] = useState<ReturnType<typeof timeEntriesToMatrixRows>>(() => {
    const normalized = normalizeScenario(initialScenario);
    const weekStart = defaultWeekStartFromScenario(normalized);
    const raw = timeEntriesToMatrixRows(normalized.harvest.time_entries, buildWeekDates(weekStart));
    return sortTimeEntryMatrixRows(raw, normalized.harvest.users, normalized.harvest.projects);
  });
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const normalized = ensureScenarioStableIds(normalizeScenario(initialScenario));
    setScenario(normalized);
    const fromHash = timeOffHashToRows(initialScenario.bamboo?.time_off_by_employee ?? {});
    setTimeOffRows(fromHash.length > 0 ? fromHash : [ emptyPtoRow() ]);
    const nextWeekStart = defaultWeekStartFromScenario(normalized);
    setTimeEntryWeekStart(nextWeekStart);
    setTimeEntryProjectFilter(PROJECT_FILTER_ALL);
    setTimeEntryMatrixRows(
      sortTimeEntryMatrixRows(
        timeEntriesToMatrixRows(normalized.harvest.time_entries, buildWeekDates(nextWeekStart)),
        normalized.harvest.users,
        normalized.harvest.projects,
      ),
    );
  }, [ serverScenarioKey, initialScenario ]);

  const pickableHarvestUsers = useMemo(
    () => scenario.harvest.users.filter((u) => isPickableHarvestUser(u)),
    [ scenario.harvest.users ],
  );
  const pickableHarvestProjects = useMemo(
    () => scenario.harvest.projects.filter((p) => isPickableHarvestProject(p)),
    [ scenario.harvest.projects ],
  );
  const harvestEmails = useMemo(
    () => [ ...new Set(scenario.harvest.users.map((u) => nz(u.email).trim()).filter(Boolean)) ],
    [ scenario.harvest.users ],
  );
  const pickableBambooEmployees = useMemo(
    () => scenario.bamboo.employees.filter((e) => isPickableBambooEmployee(e)),
    [ scenario.bamboo.employees ],
  );

  /** True when replacing would discard visible content — triggers confirm before requesting new random data. */
  const hasScenarioContentToReplace = useMemo(() => {
    if (scenarioPersistedInRedis) return true;
    if (pickableHarvestUsers.length > 0 || pickableHarvestProjects.length > 0) return true;
    if (pickableBambooEmployees.length > 0) return true;
    const hasTimeRows = scenario.harvest.time_entries.some(
      (t) => nz(t.user_harvest_id).trim() && nz(t.project_harvest_id).trim(),
    );
    if (hasTimeRows) return true;
    if (scenario.bamboo.sync_week_starts.some((w) => nz(w).trim())) return true;
    const hasPto = timeOffRows.some((r) => nz(r.employeeId).trim() && nz(r.start).trim() && nz(r.end).trim());
    return hasPto;
  }, [
    scenarioPersistedInRedis,
    pickableHarvestUsers.length,
    pickableHarvestProjects.length,
    pickableBambooEmployees.length,
    scenario.harvest.time_entries,
    scenario.bamboo.sync_week_starts,
    timeOffRows,
  ]);

  const baselineComparablePayload = useMemo(() => {
    const norm = ensureScenarioStableIds(normalizeScenario(initialScenario));
    const fromHash = timeOffHashToRows(initialScenario.bamboo?.time_off_by_employee ?? {});
    const rows = fromHash.length > 0 ? fromHash : [ emptyPtoRow() ];
    return comparableScenarioPayload(norm, rows);
  }, [ initialScenario, serverScenarioKey ]);

  const hasUnsavedChanges = useMemo(
    () => comparableScenarioPayload(scenario, timeOffRows) !== baselineComparablePayload,
    [ scenario, timeOffRows, baselineComparablePayload ],
  );

  const timeOffRowsError = useMemo(() => validateTimeOffRows(timeOffRows), [timeOffRows]);

  const dateFieldErrors = useMemo(() => {
    const projectStartsOn: Record<number, string | null> = {};
    scenario.harvest.projects.forEach((p, idx) => {
      projectStartsOn[idx] = isoCalendarDateError(p.starts_on);
    });
    return { projectStartsOn };
  }, [scenario.harvest.projects]);

  const hasDateErrors = useMemo(() => {
    return Object.values(dateFieldErrors.projectStartsOn).some(Boolean);
  }, [dateFieldErrors]);

  const hasBlockingDateIssues = hasDateErrors || Boolean(timeOffRowsError);

  const timeEntryWeekDates = useMemo(() => buildWeekDates(timeEntryWeekStart), [ timeEntryWeekStart ]);
  const timeEntryWeekDateSet = useMemo(() => new Set(timeEntryWeekDates), [ timeEntryWeekDates ]);
  const outOfWeekTimeEntryCount = useMemo(
    () => scenario.harvest.time_entries.filter((entry) => !timeEntryWeekDateSet.has(nz(entry.spent_date).trim())).length,
    [ scenario.harvest.time_entries, timeEntryWeekDateSet ],
  );
  const visibleTimeEntryMatrixRows = useMemo(() => {
    if (timeEntryProjectFilter === PROJECT_FILTER_ALL) return timeEntryMatrixRows;
    return timeEntryMatrixRows.filter((row) => nz(row.project_harvest_id).trim() === timeEntryProjectFilter);
  }, [ timeEntryMatrixRows, timeEntryProjectFilter ]);
  const canAddEmployeeRowsForProject = timeEntryProjectFilter !== PROJECT_FILTER_ALL;
  /** All scenario users with ids — not just `pickableHarvestUsers`, so brand-new rows (id assigned, email/name still empty) count as missing immediately. */
  const missingUsersForFilteredProject = useMemo(() => {
    if (!canAddEmployeeRowsForProject) return [];
    const existingUserIds = new Set(
      timeEntryMatrixRows
        .filter((row) => nz(row.project_harvest_id).trim() === timeEntryProjectFilter)
        .map((row) => nz(row.user_harvest_id).trim())
        .filter(Boolean),
    );
    return scenario.harvest.users.filter((u) => nz(u.id).trim() && !existingUserIds.has(nz(u.id).trim()));
  }, [ canAddEmployeeRowsForProject, scenario.harvest.users, timeEntryMatrixRows, timeEntryProjectFilter ]);
  const addEmployeeRowsDisabled =
    timeEntryWeekDates.length === 0
    || !canAddEmployeeRowsForProject
    || missingUsersForFilteredProject.length === 0;

  const ptoDateCellErrors = useMemo(
    () =>
      timeOffRows.map((r) => ({
        start: nz(r.start).trim() ? isoCalendarDateError(r.start) : null,
        end: nz(r.end).trim() ? isoCalendarDateError(r.end) : null,
      })),
    [timeOffRows],
  );

  function patchScenario(updater: (prev: Scenario) => Scenario) {
    setScenario((prev) => ensureScenarioStableIds(updater(prev)));
  }

  function updatePtoRow(index: number, patch: Partial<BambooPtoRow>) {
    setTimeOffRows((rows) => {
      const next = [ ...rows ];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }

  function syncBudgetSpentFromTimeEntries() {
    patchScenario((s) => {
      const outsideWeek = s.harvest.time_entries.filter(
        (entry) => !timeEntryWeekDateSet.has(nz(entry.spent_date).trim()),
      );
      const mergedEntries = [
        ...outsideWeek,
        ...matrixRowsToTimeEntries(timeEntryMatrixRows, timeEntryWeekDates),
      ];
      return recalculateBudgetSpentFromTimeEntries(
        { ...s, harvest: { ...s.harvest, time_entries: mergedEntries } },
        { asOfIso: appClockTodayIso },
      );
    });
  }

  function replaceWeekTimeEntries(matrixRows: ReturnType<typeof timeEntriesToMatrixRows>) {
    const sorted = sortTimeEntryMatrixRows(matrixRows, scenario.harvest.users, scenario.harvest.projects);
    setTimeEntryMatrixRows(sorted);
    if (timeEntryWeekDates.length === 0) return;
    patchScenario((s) => {
      const outsideWeek = s.harvest.time_entries.filter((entry) => !timeEntryWeekDateSet.has(nz(entry.spent_date).trim()));
      const updatedWeek = matrixRowsToTimeEntries(sorted, timeEntryWeekDates);
      return {
        ...s,
        harvest: {
          ...s.harvest,
          time_entries: [ ...outsideWeek, ...updatedWeek ],
        },
      };
    });
  }

  function addTimeEntryMatrixRow() {
    if (!canAddEmployeeRowsForProject || timeEntryWeekDates.length === 0) return;
    if (missingUsersForFilteredProject.length === 0) return;
    const newRows = missingUsersForFilteredProject.map((user) => {
      const row = emptyTimeEntryMatrixRow(timeEntryWeekDates);
      row.project_harvest_id = timeEntryProjectFilter;
      row.user_harvest_id = user.id;
      Object.assign(row.hoursByDate, randomWeekHoursUnderCap(timeEntryWeekDates, 40));
      return row;
    });
    replaceWeekTimeEntries([ ...timeEntryMatrixRows, ...newRows ]);
  }

  function removeTimeEntryMatrixRow(rowKey: string) {
    replaceWeekTimeEntries(timeEntryMatrixRows.filter((row) => row.rowKey !== rowKey));
  }

  function updateTimeEntryMatrixRow(
    rowKey: string,
    patch: Partial<Pick<TimeEntry, "user_harvest_id" | "project_harvest_id">>,
  ) {
    replaceWeekTimeEntries(
      timeEntryMatrixRows.map((row) => {
        if (row.rowKey !== rowKey) return row;
        return { ...row, ...patch };
      }),
    );
  }

  function updateTimeEntryHoursCell(rowKey: string, spentDate: string, value: string) {
    replaceWeekTimeEntries(updateMatrixCell(timeEntryMatrixRows, rowKey, spentDate, value));
  }

  function saveScenario() {
    if (hasBlockingDateIssues) {
      window.alert(
        timeOffRowsError
          || "Fix invalid dates (YYYY-MM-DD) before saving.",
      );
      return;
    }
    const timeOff = timeOffRowsToHash(timeOffRows);
    const payload = scenarioToPayload({
      ...scenario,
      bamboo: { ...scenario.bamboo, time_off_by_employee: timeOff, sync_week_starts: scenario.bamboo.sync_week_starts },
    });
    setBusy("save");
    router.patch("/admin/mock_integration", { scenario: payload }, {
      preserveScroll: true,
      onFinish: () => setBusy(null),
    });
  }

  function applyMocks() {
    if (hasBlockingDateIssues) {
      window.alert(
        timeOffRowsError
          || "Fix invalid dates on this page and save the scenario before applying (Apply uses the last saved Redis copy).",
      );
      return;
    }
    setBusy("apply");
    router.post("/admin/mock_integration/apply", {}, { preserveScroll: true, onFinish: () => setBusy(null) });
  }

  function clearMockserver() {
    setBusy("clear-ms");
    router.post("/admin/mock_integration/clear_mockserver", {}, { preserveScroll: true, onFinish: () => setBusy(null) });
  }

  function clearScenario() {
    if (!window.confirm("Clear scenario from Redis?")) return;
    setBusy("clear-scenario");
    router.delete("/admin/mock_integration/scenario", { preserveScroll: true, onFinish: () => setBusy(null) });
  }

  function exportJson() {
    window.location.href = "/admin/mock_integration/export";
  }

  function refreshMockStackFull() {
    if (hasBlockingDateIssues) {
      window.alert(
        timeOffRowsError
          || "Fix invalid dates (YYYY-MM-DD) before running the full refresh.",
      );
      return;
    }
    if (hasScenarioContentToReplace) {
      const ok = window.confirm(
        "Run full refresh? This writes new random data to Redis and MockServer, then syncs users, projects, "
        + "and the weekly planner for the ISO week(s) generated by this randomize run (not other weeks in the form).",
      );
      if (!ok) return;
    }
    setBusy("full-refresh");
    router.post("/admin/mock_integration/refresh_mock_stack", {}, {
      preserveScroll: true,
      onFinish: () => setBusy(null),
    });
  }

  function randomizeData() {
    if (hasScenarioContentToReplace) {
      const ok = window.confirm(
        "Replace the mock data currently on this page with new random Harvest + Bamboo data? "
        + "Nothing is written to Redis until you click Save scenario.",
      );
      if (!ok) return;
    }
    setBusy("randomize");
    router.post("/admin/mock_integration/randomize", {}, {
      preserveScroll: true,
      onFinish: () => setBusy(null),
    });
  }

  function importFile(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const form = ev.currentTarget;
    const fd = new FormData(form);
    setBusy("import");
    router.post("/admin/mock_integration/import", fd, {
      forceFormData: true,
      preserveScroll: true,
      onFinish: () => setBusy(null),
    });
  }

  function revertChanges() {
    if (!scenarioPersistedInRedis || !hasUnsavedChanges) return;
    setBusy("revert");
    router.reload({
      only: [ "scenario", "scenarioPersistedInRedis", "mockserverDashboardUrl" ],
      onFinish: () => setBusy(null),
    });
  }

  return (
    <>
      <Head title="Mock integrations (dev)" />
      <AppLayout
        user={auth.user}
        activeModule="weekly_planner"
        onModuleChange={() => {}}
        pageTitle="Mock Harvest / Bamboo (dev only)"
        contextControls={null}
        pageActions={(
          <Flex gap="2">
            <Link href="/">
              <Button variant="outline">Back to Dashboard</Button>
            </Link>
          </Flex>
        )}
      >
        <Flex direction="column" gap="4" p="4">
          {flash?.notice && (
            <Callout.Root color="green">
              <Callout.Text>{flash.notice}</Callout.Text>
            </Callout.Root>
          )}
          {flash?.alert && (
            <Callout.Root color="red">
              <Callout.Text>{flash.alert}</Callout.Text>
            </Callout.Root>
          )}
          {(hasDateErrors || timeOffRowsError) && (
            <Callout.Root color="red" variant="soft">
              <Callout.Text>
                Fix date fields: project <strong>starts_on</strong> and time entry <strong>spent_date</strong> must be empty or valid{" "}
                <code>YYYY-MM-DD</code>.
                {timeOffRowsError && (
                  <> {timeOffRowsError}</>
                )}
              </Callout.Text>
            </Callout.Root>
          )}

          <Callout.Root color="amber">
            <Callout.Text>
              Development-only. Point integration bases at MockServer via compose overlay; use real-shaped credentials in Settings
              (dummy values OK). MockServer requires auth headers but does not validate secrets.
            </Callout.Text>
          </Callout.Root>

          <Text size="2" color="gray">
            <strong>Save scenario</strong> stores this form in <strong>Redis</strong> (your draft; survives until you clear it or re-import).
            {" "}
            <strong>Apply to MockServer</strong> resets MockServer and uploads <strong>generated expectations</strong> from that scenario plus the repo
            {" "}
            <code style={{ fontSize: "0.9em" }}>initializer.json</code> baseline — MockServer does not serve your new stubs until you Apply.
            {" "}
            <strong>Randomize Data</strong> fills the form with Faker-built users, projects, 40h/weekday time per project (prior ISO week), Bamboo directory rows, and no PTO —{" "}
            <strong>only in the browser</strong>
            {" "}
            until you Save scenario to Redis.
          </Text>

          <Flex gap="3" wrap="wrap" align="center">
            <Button
              onClick={refreshMockStackFull}
              disabled={busy !== null || hasBlockingDateIssues}
              variant="solid"
              color="green"
              title="Randomize, save to Redis, apply to MockServer, then sync users, projects, and weekly planner for the week(s) this randomize run creates"
            >
              {busy === "full-refresh" ? "Refreshing…" : "Randomize, save, apply & sync"}
            </Button>
            <Button
              onClick={randomizeData}
              disabled={busy !== null}
              variant="soft"
              color="blue"
              title="Load random mock data into the form (confirm if you already have data on the page)"
            >
              {busy === "randomize" ? "Randomizing…" : "Randomize Data"}
            </Button>
            <Button onClick={saveScenario} disabled={busy !== null || hasBlockingDateIssues}>
              {busy === "save" ? "Saving…" : "Save scenario"}
            </Button>
            <Button
              onClick={revertChanges}
              disabled={busy !== null || !scenarioPersistedInRedis || !hasUnsavedChanges}
              variant="outline"
            >
              {busy === "revert" ? "Reverting…" : "Revert changes"}
            </Button>
            <Button onClick={applyMocks} disabled={busy !== null || hasBlockingDateIssues} variant="solid">
              {busy === "apply" ? "Applying…" : "Apply to MockServer"}
            </Button>
            <Button onClick={clearMockserver} disabled={busy !== null} variant="outline" color="orange">
              {busy === "clear-ms" ? "…" : "Reset MockServer"}
            </Button>
            <Button onClick={clearScenario} disabled={busy !== null} variant="outline" color="red">
              {busy === "clear-scenario" ? "…" : "Clear scenario (Redis)"}
            </Button>
            <Button asChild variant="soft">
              <a href={mockserverDashboardUrl} target="_blank" rel="noreferrer">MockServer dashboard</a>
            </Button>
            <Button onClick={exportJson} variant="ghost" type="button">Export JSON</Button>
          </Flex>
          <Text size="1" color="gray">
            Dashboard URL comes from <code>MOCKSERVER_DASHBOARD_URL</code> or defaults to{" "}
            <code>http://127.0.0.1:1090/mockserver/dashboard</code> (host port mapped in dev compose). Set env if your published port differs.
          </Text>

          <Box>
            <Heading size="4" mb="2">Import</Heading>
            <form onSubmit={importFile}>
              <Flex gap="2" align="end" wrap="wrap">
                <Box>
                  <Text size="2" weight="medium" as="div">Scenario JSON file</Text>
                  <input name="file" type="file" accept="application/json,.json" required style={{ marginTop: 8 }} />
                </Box>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="checkbox" name="apply_after_import" value="1" />
                  <Text size="2">Apply after import</Text>
                </label>
                <Button type="submit" disabled={busy !== null}>{busy === "import" ? "Importing…" : "Import"}</Button>
              </Flex>
            </form>
          </Box>

          <Separator size="4" />

          <Heading size="5">Harvest</Heading>

          <Heading size="4" mt="3">Users</Heading>
          <Text size="2" color="gray">
            Harvest API ids are assigned automatically when you edit (hidden — no need to type them).
          </Text>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>first</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>last</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>hrs/wk</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {scenario.harvest.users.map((u, idx) => (
                <Table.Row key={`u-${idx}`}>
                  {(["email", "first_name", "last_name", "baseline_weekly_hours"] as const).map((field) => (
                    <Table.Cell key={field}>
                      <TextField.Root
                        value={u[field]}
                        onChange={(e) => patchScenario((s) => {
                          const users = [ ...s.harvest.users ];
                          users[idx] = { ...users[idx], [field]: e.target.value };
                          return { ...s, harvest: { ...s.harvest, users } };
                        })}
                      />
                    </Table.Cell>
                  ))}
                  <Table.Cell>
                    <Button
                      size="1"
                      color="red"
                      variant="soft"
                      onClick={() => {
                        const removedUser = scenario.harvest.users[idx];
                        const removedIds = new Set(
                          scenario.bamboo.employees
                            .filter((e) => emailsMatch(e.workEmail, removedUser.email))
                            .map((e) => nz(e.id).trim())
                            .filter(Boolean),
                        );
                        setTimeOffRows((rows) => rows.filter((r) => !removedIds.has(nz(r.employeeId).trim())));
                        const harvestUserId = nz(removedUser.id).trim();
                        patchScenario((s) => {
                          const time_entries = harvestUserId
                            ? s.harvest.time_entries.filter((t) => nz(t.user_harvest_id).trim() !== harvestUserId)
                            : s.harvest.time_entries;
                          return {
                            ...s,
                            harvest: {
                              ...s.harvest,
                              users: s.harvest.users.filter((_, i) => i !== idx),
                              time_entries,
                            },
                            bamboo: {
                              ...s.bamboo,
                              employees: s.bamboo.employees.filter((e) => !emailsMatch(e.workEmail, removedUser.email)),
                            },
                          };
                        });
                      }}
                    >
                      Remove
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
          <Flex justify="start" style={{ alignSelf: "flex-start" }}>
            <Button size="1" variant="outline" onClick={() => patchScenario((s) => ({
              ...s,
              harvest: { ...s.harvest, users: [ ...s.harvest.users, emptyUser() ] },
            }))}
            >
              Add user
            </Button>
          </Flex>

          <Flex align="center" justify="between" gap="3" mt="3" wrap="wrap">
            <Heading size="4">Projects</Heading>
            <Button size="1" variant="soft" onClick={syncBudgetSpentFromTimeEntries}>
              Recalculate budget_spent from time entries
            </Button>
          </Flex>
          <Text size="1" color="gray" as="p" style={{ marginTop: 4 }}>
            Edit budget_spent manually anytime (opening balance before logged hours). The button sets
            budget_spent to that opening plus all scenario time entries through the environment app clock
            (then Save scenario and Apply). Editing budget_spent clears the locked opening so the next
            recalc uses your new value.
          </Text>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>budget</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>budget_spent</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>starts_on (YYYY-MM-DD)</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {scenario.harvest.projects.map((p, idx) => (
                <Table.Row key={`p-${idx}`}>
                  {(["name", "budget", "budget_spent"] as const).map((field) => (
                    <Table.Cell key={field}>
                      <TextField.Root
                        value={p[field]}
                        onChange={(e) => patchScenario((s) => {
                          const projects = [ ...s.harvest.projects ];
                          const patch: HarvestProject = { ...projects[idx], [field]: e.target.value };
                          if (field === "budget_spent") patch.budget_spent_anchor = "";
                          projects[idx] = patch;
                          return { ...s, harvest: { ...s.harvest, projects } };
                        })}
                      />
                    </Table.Cell>
                  ))}
                  <Table.Cell>
                    {(() => {
                      const raw = nz(p.starts_on).trim();
                      const err = dateFieldErrors.projectStartsOn[idx];
                      const textFallback = Boolean(raw && err);
                      if (textFallback) {
                        return (
                          <TextField.Root
                            value={p.starts_on}
                            color="red"
                            placeholder="YYYY-MM-DD"
                            onChange={(e) => patchScenario((s) => {
                              const projects = [ ...s.harvest.projects ];
                              projects[idx] = { ...projects[idx], starts_on: e.target.value };
                              return { ...s, harvest: { ...s.harvest, projects } };
                            })}
                          />
                        );
                      }
                      return (
                        <input
                          type="date"
                          value={raw}
                          onChange={(e) => patchScenario((s) => {
                            const projects = [ ...s.harvest.projects ];
                            projects[idx] = { ...projects[idx], starts_on: e.target.value };
                            return { ...s, harvest: { ...s.harvest, projects } };
                          })}
                          aria-label="Project starts on"
                          style={dateInputStyle(Boolean(err))}
                        />
                      );
                    })()}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      size="1"
                      color="red"
                      variant="soft"
                      onClick={() => patchScenario((s) => {
                        const removed = s.harvest.projects[idx];
                        const pid = nz(removed.id).trim();
                        const time_entries = pid
                          ? s.harvest.time_entries.filter((t) => nz(t.project_harvest_id).trim() !== pid)
                          : s.harvest.time_entries;
                        return {
                          ...s,
                          harvest: {
                            ...s.harvest,
                            projects: s.harvest.projects.filter((_, i) => i !== idx),
                            time_entries,
                          },
                        };
                      })}
                    >
                      Remove
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
          {scenario.harvest.projects.some((_, idx) => dateFieldErrors.projectStartsOn[idx]) && (
            <Text size="1" color="red">starts_on: use YYYY-MM-DD or leave blank.</Text>
          )}
          <Flex justify="start" style={{ alignSelf: "flex-start" }}>
            <Button size="1" variant="outline" onClick={() => patchScenario((s) => ({
              ...s,
              harvest: { ...s.harvest, projects: [ ...s.harvest.projects, emptyProject() ] },
            }))}
            >
              Add project
            </Button>
          </Flex>

          <Heading size="4" mt="3">Time entries</Heading>
          <Text size="2" color="gray">
            Weekly matrix mode: one row per <strong>User + Project</strong>, with Mon-Fri hours.
          </Text>
          <Flex gap="3" wrap="wrap" align="end">
            <Box>
              <Text size="2" weight="medium" as="div">Week start (Monday)</Text>
              <input
                type="date"
                value={timeEntryWeekStart}
                onChange={(e) => {
                  const nextWeekStart = e.target.value;
                  setTimeEntryWeekStart(nextWeekStart);
                  setTimeEntryMatrixRows(
                    sortTimeEntryMatrixRows(
                      timeEntriesToMatrixRows(scenario.harvest.time_entries, buildWeekDates(nextWeekStart)),
                      scenario.harvest.users,
                      scenario.harvest.projects,
                    ),
                  );
                }}
                aria-label="Time entry week start"
                style={{
                  ...dateInputStyle(Boolean(timeEntryWeekStart && timeEntryWeekDates.length === 0)),
                  marginTop: 8,
                  minWidth: 180,
                }}
              />
            </Box>
            <Box style={{ minWidth: 260 }}>
              <Text size="2" weight="medium" as="div">Project filter</Text>
              <Select.Root value={timeEntryProjectFilter} onValueChange={setTimeEntryProjectFilter}>
                <Select.Trigger placeholder="All projects" />
                <Select.Content position="popper">
                  <Select.Item value={PROJECT_FILTER_ALL}>All projects</Select.Item>
                  {pickableHarvestProjects.map((project) => (
                    <Select.Item key={project.id} value={project.id}>
                      {harvestProjectDisplayLabel(project)}
                    </Select.Item>
                  ))}
                  {timeEntryProjectFilter !== PROJECT_FILTER_ALL
                  && !pickableHarvestProjects.some((project) => project.id === timeEntryProjectFilter) && (
                    <Select.Item value={timeEntryProjectFilter}>Unknown project (current filter)</Select.Item>
                  )}
                </Select.Content>
              </Select.Root>
            </Box>
            {outOfWeekTimeEntryCount > 0 && (
              <Text size="2" color="gray">
                {outOfWeekTimeEntryCount} time entr{outOfWeekTimeEntryCount === 1 ? "y is" : "ies are"} outside this week and kept unchanged.
              </Text>
            )}
          </Flex>
          {timeEntryWeekDates.length === 0 && (
            <Text size="1" color="red">Pick a valid Monday date to edit the weekly matrix.</Text>
          )}
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>User</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Project</Table.ColumnHeaderCell>
                {timeEntryWeekDates.map((d, idx) => (
                  <Table.ColumnHeaderCell key={d}>
                    {["Mon", "Tue", "Wed", "Thu", "Fri"][idx]} ({d})
                  </Table.ColumnHeaderCell>
                ))}
                <Table.ColumnHeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {visibleTimeEntryMatrixRows.map((row) => (
                <Table.Row key={row.rowKey}>
                  <Table.Cell>
                    {pickableHarvestUsers.length > 0 ? (
                      <Select.Root
                        value={nz(row.user_harvest_id).trim() ? row.user_harvest_id : SELECT_NONE}
                        onValueChange={(v) => updateTimeEntryMatrixRow(row.rowKey, { user_harvest_id: v === SELECT_NONE ? "" : v })}
                      >
                        <Select.Trigger placeholder="User" />
                        <Select.Content position="popper">
                          <Select.Item value={SELECT_NONE}>—</Select.Item>
                          {pickableHarvestUsers.map((u) => (
                            <Select.Item key={u.id} value={u.id}>
                              {harvestUserDisplayLabel(u)}
                            </Select.Item>
                          ))}
                          {nz(row.user_harvest_id).trim()
                          && !pickableHarvestUsers.some((u) => u.id === row.user_harvest_id) && (
                            <Select.Item value={row.user_harvest_id}>
                              {(() => {
                                const legacy = scenario.harvest.users.find((u) => u.id === row.user_harvest_id);
                                return legacy ? harvestUserDisplayLabel(legacy) : "Unknown user (legacy)";
                              })()}
                            </Select.Item>
                          )}
                        </Select.Content>
                      </Select.Root>
                    ) : (
                      <TextField.Root
                        value={row.user_harvest_id}
                        onChange={(e) => updateTimeEntryMatrixRow(row.rowKey, { user_harvest_id: e.target.value })}
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {pickableHarvestProjects.length > 0 ? (
                      <Select.Root
                        value={nz(row.project_harvest_id).trim() ? row.project_harvest_id : SELECT_NONE}
                        onValueChange={(v) => updateTimeEntryMatrixRow(row.rowKey, { project_harvest_id: v === SELECT_NONE ? "" : v })}
                      >
                        <Select.Trigger placeholder="Project" />
                        <Select.Content position="popper">
                          <Select.Item value={SELECT_NONE}>—</Select.Item>
                          {pickableHarvestProjects.map((p) => (
                            <Select.Item key={p.id} value={p.id}>
                              {harvestProjectDisplayLabel(p)}
                            </Select.Item>
                          ))}
                          {nz(row.project_harvest_id).trim()
                          && !pickableHarvestProjects.some((p) => p.id === row.project_harvest_id) && (
                            <Select.Item value={row.project_harvest_id}>
                              {(() => {
                                const legacy = scenario.harvest.projects.find((p) => p.id === row.project_harvest_id);
                                return legacy ? harvestProjectDisplayLabel(legacy) : "Unknown project (legacy)";
                              })()}
                            </Select.Item>
                          )}
                        </Select.Content>
                      </Select.Root>
                    ) : (
                      <TextField.Root
                        value={row.project_harvest_id}
                        onChange={(e) => updateTimeEntryMatrixRow(row.rowKey, { project_harvest_id: e.target.value })}
                      />
                    )}
                  </Table.Cell>
                  {timeEntryWeekDates.map((spentDate) => (
                    <Table.Cell key={`${row.rowKey}-${spentDate}`}>
                      <TextField.Root
                        value={row.hoursByDate[spentDate] ?? ""}
                        placeholder="0"
                        onChange={(e) => updateTimeEntryHoursCell(row.rowKey, spentDate, e.target.value)}
                      />
                    </Table.Cell>
                  ))}
                  <Table.Cell>
                    <Button size="1" color="red" variant="soft" onClick={() => removeTimeEntryMatrixRow(row.rowKey)}>
                      Remove
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
          <Flex justify="start" style={{ alignSelf: "flex-start" }}>
            <Button
              size="1"
              variant="outline"
              onClick={addTimeEntryMatrixRow}
              disabled={addEmployeeRowsDisabled}
              title="Creates one row per missing employee for the filtered project with random Mon–Fri hours (12–40h total, never over 40h per row)."
            >
              {canAddEmployeeRowsForProject
                ? `Add missing employees (${missingUsersForFilteredProject.length})`
                : "Select a project to add employee rows"}
            </Button>
          </Flex>
          {canAddEmployeeRowsForProject && missingUsersForFilteredProject.length === 0 && (
            <Text size="1" color="gray">All employees already have a row for this project.</Text>
          )}

          <Separator size="4" />

          <Heading size="5">BambooHR</Heading>

          <Heading size="4" mt="3">Employees</Heading>
          <Text size="2" color="gray">
            Bamboo directory rows for this <strong>mock scenario</strong> (saved JSON — not a live API call). Employee ids match Harvest users when emails align — assigned automatically.
          </Text>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>workEmail</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>displayName</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>firstName</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>lastName</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {scenario.bamboo.employees.map((row, idx) => (
                <Table.Row key={`e-${idx}`}>
                  <Table.Cell>
                    {harvestEmails.length > 0 ? (
                      <Select.Root
                        value={nz(row.workEmail).trim() ? row.workEmail : SELECT_NONE}
                        onValueChange={(v) => patchScenario((s) => {
                          const employees = [ ...s.bamboo.employees ];
                          employees[idx] = { ...employees[idx], workEmail: v === SELECT_NONE ? "" : v };
                          return { ...s, bamboo: { ...s.bamboo, employees } };
                        })}
                      >
                        <Select.Trigger placeholder="Scenario user email" />
                        <Select.Content position="popper">
                          <Select.Item value={SELECT_NONE}>—</Select.Item>
                          {harvestEmails.map((em) => (
                            <Select.Item key={em} value={em}>
                              {(() => {
                                const u = scenario.harvest.users.find((x) => emailsMatch(x.email, em));
                                return u ? harvestUserDisplayLabel(u) : em;
                              })()}
                            </Select.Item>
                          ))}
                          {nz(row.workEmail).trim()
                          && !harvestEmails.some((em) => emailsMatch(em, row.workEmail)) && (
                            <Select.Item value={row.workEmail}>{row.workEmail} (current)</Select.Item>
                          )}
                        </Select.Content>
                      </Select.Root>
                    ) : (
                      <TextField.Root
                        value={row.workEmail}
                        onChange={(e) => patchScenario((s) => {
                          const employees = [ ...s.bamboo.employees ];
                          employees[idx] = { ...employees[idx], workEmail: e.target.value };
                          return { ...s, bamboo: { ...s.bamboo, employees } };
                        })}
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <TextField.Root
                      value={row.displayName}
                      onChange={(e) => patchScenario((s) => {
                        const employees = [ ...s.bamboo.employees ];
                        employees[idx] = { ...employees[idx], displayName: e.target.value };
                        return { ...s, bamboo: { ...s.bamboo, employees } };
                      })}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <TextField.Root
                      value={row.firstName}
                      onChange={(e) => patchScenario((s) => {
                        const employees = [ ...s.bamboo.employees ];
                        employees[idx] = { ...employees[idx], firstName: e.target.value };
                        return { ...s, bamboo: { ...s.bamboo, employees } };
                      })}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <TextField.Root
                      value={row.lastName}
                      onChange={(e) => patchScenario((s) => {
                        const employees = [ ...s.bamboo.employees ];
                        employees[idx] = { ...employees[idx], lastName: e.target.value };
                        return { ...s, bamboo: { ...s.bamboo, employees } };
                      })}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Button size="1" color="red" variant="soft" onClick={() => patchScenario((s) => ({
                      ...s,
                      bamboo: { ...s.bamboo, employees: s.bamboo.employees.filter((_, i) => i !== idx) },
                    }))}
                    >
                      Remove
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
          <Flex gap="2" wrap="wrap" align="center" style={{ alignSelf: "flex-start" }}>
            <Button size="1" variant="outline" onClick={() => patchScenario((s) => ({
              ...s,
              bamboo: { ...s.bamboo, employees: [ ...s.bamboo.employees, emptyEmployee() ] },
            }))}
            >
              Add employee
            </Button>
            <Button
              size="1"
              variant="soft"
              onClick={() => patchScenario((s) => syncEmployeesFromHarvestUsers(ensureScenarioStableIds(s)))}
            >
              Fill from Harvest section (scenario data)
            </Button>
          </Flex>

          <Heading size="4" mt="3">Time off (PTO)</Heading>
          <Text size="2" color="gray">
            One row per request. Pick an employee from Bamboo rows above, or type a legacy id if needed. Dates use the browser date picker.
          </Text>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Employee</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Start</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>End</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Custom status fields</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {timeOffRows.map((row, idx) => (
                <Table.Row key={row.rowKey}>
                  <Table.Cell>
                    {pickableBambooEmployees.length > 0 ? (
                      <Select.Root
                        value={nz(row.employeeId).trim() ? row.employeeId : SELECT_NONE}
                        onValueChange={(v) => updatePtoRow(idx, {
                          employeeId: v === SELECT_NONE ? "" : v,
                        })}
                      >
                        <Select.Trigger placeholder="Employee" />
                        <Select.Content position="popper">
                          <Select.Item value={SELECT_NONE}>—</Select.Item>
                          {pickableBambooEmployees.map((emp) => (
                            <Select.Item key={emp.id} value={emp.id}>
                              {bambooEmployeeDisplayLabel(emp)}
                            </Select.Item>
                          ))}
                          {nz(row.employeeId).trim()
                          && !pickableBambooEmployees.some((e) => nz(e.id) === nz(row.employeeId)) && (
                            <Select.Item value={row.employeeId}>
                              {(() => {
                                const emp = scenario.bamboo.employees.find((e) => nz(e.id) === nz(row.employeeId));
                                return emp ? bambooEmployeeDisplayLabel(emp) : "Unknown employee (legacy)";
                              })()}
                            </Select.Item>
                          )}
                        </Select.Content>
                      </Select.Root>
                    ) : (
                      <TextField.Root
                        value={row.employeeId}
                        placeholder="Bamboo employee id"
                        onChange={(e) => updatePtoRow(idx, { employeeId: e.target.value })}
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <input
                      type="date"
                      value={row.start}
                      onChange={(e) => updatePtoRow(idx, { start: e.target.value })}
                      aria-label="PTO start"
                      style={{
                        width: "100%",
                        minHeight: 32,
                        borderRadius: "var(--radius-2)",
                        border: ptoDateCellErrors[idx]?.start ? "1px solid var(--red-9)" : "1px solid var(--gray-a7)",
                        padding: "0 8px",
                        fontSize: 14,
                        background: "var(--gray-2)",
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <input
                      type="date"
                      value={row.end}
                      onChange={(e) => updatePtoRow(idx, { end: e.target.value })}
                      aria-label="PTO end"
                      style={{
                        width: "100%",
                        minHeight: 32,
                        borderRadius: "var(--radius-2)",
                        border: ptoDateCellErrors[idx]?.end ? "1px solid var(--red-9)" : "1px solid var(--gray-a7)",
                        padding: "0 8px",
                        fontSize: 14,
                        background: "var(--gray-2)",
                      }}
                    />
                  </Table.Cell>
                  <Table.Cell style={{ minWidth: 140 }}>
                    <Select.Root
                      value={row.statusPreset}
                      onValueChange={(v) => updatePtoRow(idx, {
                        statusPreset: v,
                        ...(v !== "custom" ? { customStatusId: "", customStatusName: "" } : {}),
                      })}
                    >
                      <Select.Trigger />
                      <Select.Content position="popper">
                        {PTO_STATUS_PRESETS.map((p) => (
                          <Select.Item key={p.value} value={p.value}>{p.name}</Select.Item>
                        ))}
                        <Select.Item value="custom">Custom…</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </Table.Cell>
                  <Table.Cell>
                    {row.statusPreset === "custom" ? (
                      <Flex direction="column" gap="1">
                        <TextField.Root
                          size="1"
                          placeholder="status id"
                          value={row.customStatusId}
                          onChange={(e) => updatePtoRow(idx, { customStatusId: e.target.value })}
                        />
                        <TextField.Root
                          size="1"
                          placeholder="status name"
                          value={row.customStatusName}
                          onChange={(e) => updatePtoRow(idx, { customStatusName: e.target.value })}
                        />
                      </Flex>
                    ) : (
                      <Text size="1" color="gray">—</Text>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      size="1"
                      color="red"
                      variant="soft"
                      onClick={() => setTimeOffRows((rows) => (rows.length <= 1 ? [ emptyPtoRow() ] : rows.filter((_, i) => i !== idx)))}
                    >
                      Remove
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
          <Flex justify="start" style={{ alignSelf: "flex-start" }}>
            <Button
              size="1"
              variant="outline"
              onClick={() => setTimeOffRows((rows) => [ ...rows, emptyPtoRow() ])}
            >
              Add PTO row
            </Button>
          </Flex>
        </Flex>
      </AppLayout>
    </>
  );
}
